"use server";

import { signIn, signOut } from "@/auth/auth";
import { AuthError } from "next-auth";
import { z } from "zod";

// Auth.js normaliza el email en `sendToken` con `.toLowerCase().trim()` antes de almacenar
// el `identifier` en `verification_tokens`. Aplicamos la misma normalización para que el
// lookup en el callback coincida (la pantalla de código manda `email` como hidden field
// directamente al endpoint de Auth.js).
const emailSchema = z.object({
  email: z
    .string()
    .email()
    .transform((v) => v.toLowerCase().trim()),
});

export interface LoginState {
  status: "idle" | "sent" | "error";
  message?: string;
  /** Email enviado, propagado a la pantalla de código. */
  email?: string;
}

/**
 * Inicia el flujo de email-code: dispara `signIn("email", { email })` para que Auth.js
 * genere el código (6 dígitos), lo hashee con sha256(code + AUTH_SECRET) y lo persista,
 * y lo envíe por Resend. El callback `signIn` rechaza emails que no estén en
 * `super_admins`, así que un email no autorizado nunca recibe código.
 */
export async function login(
  _prev: LoginState | undefined,
  formData: FormData,
): Promise<LoginState> {
  const parsed = emailSchema.safeParse(Object.fromEntries(formData));
  if (!parsed.success) {
    return { status: "error", message: "Email inválido." };
  }
  const { email } = parsed.data;

  try {
    await signIn("email", {
      email,
      redirectTo: "/",
      redirect: false,
    });
    return {
      status: "sent",
      email,
      message: "Si tu email está autorizado, recibirás un código en breve.",
    };
  } catch (error) {
    if (error instanceof AuthError) {
      // Mensaje genérico para no filtrar qué emails están autorizados.
      return {
        status: "sent",
        email,
        message: "Si tu email está autorizado, recibirás un código en breve.",
      };
    }
    throw error;
  }
}

export async function loginWithGoogle(): Promise<void> {
  await signIn("google", { redirectTo: "/" });
}

export async function logout(): Promise<void> {
  await signOut({ redirectTo: "/login" });
}
